const _ = require("lodash");
const moment = require("moment");
const {
	TEST_QUIT_COOLDOWN_DAYS,
	TEST_FAIL_COOLDOWN_DAYS,
	BOOK_TEST_PART_1_MIN_MINUTES,
	BOOK_TEST_PART_2_MIN_MINUTES,
	TUTORIAL_BLOCK_BUCKS,
	TUTORIAL_BLOCK_XP,
} = require("./Constants");
const GradeChapter = require("./GradeChapter");
const ChapterSection = require("./ChapterSection");

// Result types that are not games (i.e. have multiple sets)
const MULTISET_RESULT_TYPES = [
	"standard",
	"standardSequential",
	"hiddenMessage",
	"puzzle",
	"test",
	"bookTest",
];

const TEST_RANGES = {
	"16": [12, 16],
	"20": [15, 20],
};

const BOOK_TEST_RANGES = {
	"17": [15, 17],
};

/* Rewards */

// some of these aren't part of logic
const TRIAL_XP = 5;
const THROTTLED_TRIAL_XP = 1;

/* limits for rewards */
const MAX_TRIALS_XP = 250; // the maximum rewards for successfully completing a block that aren't from stars
const MAX_TRIAL_XP_AFTER_ALL_STARS = 1;
const MAX_COMPLETIONS_BEFORE_XP_LIMITED = 4;
const MAX_COMPLETIONS_ON_3_STAR_TEST_BEFORE_XP_LIMITED = 2;

// First star gets 3 bucks, 2nd gets 2 bucks, 3rd star earned gets 2 bucks.
const BUCKS_FOR_STARS = [3, 2, 2];
const BUCKS_FOR_CELESTIALS = {
	"1": 5,
	"2": 7,
	"3": 9,
	"4": 11,
};
const XP_FOR_CELESTIALS = {
	"1": 100,
	"2": 150,
	"3": 200,
	"4": 250,
};
const MAX_CELESTIAL_XP =
	MAX_TRIALS_XP + Math.max(...Object.values(XP_FOR_CELESTIALS));
const DEFAULT_TRIAL_XP = 5;

const STAR_XP = 50;

// Initially unlocked blocks at when a chapter is unlocked
const INITIAL_UNLOCKED_BLOCK_COUNT = 1;

// The number of stars required in all lessons the preceding chunk of core
// lessons to unlock a given chunk of enrichment lessons
const MIN_STARS_PER_BLOCK_FOR_ENRICHMENT = 1;

// Helper for rendering red/green/gray buttons at the top of the problem page
// given data for a partially completed set. Pass the following:
// - problems: an array of states of problems that should include
//   outcome if they are complete which is "correct" if they were correct and trials if they are in progress.
// Returns an array of objects with keys hasStar, isComplete, and isCorrect,
// all with boolean values.
const getProgressProps = function(problems, hasIntervened, resultIsComplete) {
	const progressProps = [];
	if (!problems) return;
	for (let i = 0; i < problems.length; i++) {
		if (problems[i].isLoading) {
			progressProps.push({
				isPartiallyComplete: false,
				isComplete: false,
				percentComplete: 0,
				isCorrect: false,
				isSkipped: false,
				isSaved: false,
				isLoading: true,
			});
		} else {
			const percentComplete = resultIsComplete
				? 1
				: problems[i].trials.length / problems[i].model.numberOfAttempts;
			progressProps.push({
				isPartiallyComplete:
					!problems[i].outcome && !!problems[i].trials.length,
				isComplete: !!problems[i].outcome,
				percentComplete,
				isCorrect: problems[i].outcome === "correct",
				isSkipped: problems[i].outcome === "skipped",
				isSaved: !!problems[i].saved,
				isLoading: false,
			});
		}
	}
	return progressProps;
};

// Helper for checking a list of problems to see if the result should be intervened:
// Takes in an array of problem objects that must have an outcome
// Returns true if an intervention should occur.
const genericShouldIntervene = function(problems) {
	const completedProblems = problems.filter(p => p.outcome);

	const total = problems.length;
	const completed = completedProblems.length;
	const incorrect = completedProblems.filter(p =>
		_.includes(["incorrect", "giveup"], p.outcome)
	).length;
	// Cutoffs are of the form [num wrong, max num finished, applicable if at least this many total problems]
	// [2, 3, 5] => intervene if they've gotten at least 2 wrong, have completed at most 3, and the set has at least 5 problems
	const cutoffs = [
		[2, 3, 5],
		[3, 5, 8],
		[4, 7, 11],
	];
	return _.some(
		cutoffs,
		c => incorrect >= c[0] && completed <= c[1] && total >= c[2]
	);
};

// Helper for computing the number of stars to award to a finished set. Pass
// the following:
// - progress: an array of booleans with length equal to the number of problems
//   in the set and giving whether each problem was gotten right.
// - availableStarCutoffs: array giving the number of problems one must get right to
//   obtain the available stars
// Returns a number 0 to 3.
const getStars = function(progress, availableStarCutoffs) {
	const totalCorrect = progress.filter(Boolean).length;
	return availableStarCutoffs.filter(cutoff => totalCorrect >= cutoff).length;
};

const getDefaultStarCutoffs = function(blockType, problemsPerSet) {
	const n = problemsPerSet;
	if (blockType === "challenge") {
		return [7, 4, 2]; //Assuming all challenge sets have 10 problems
	} else if (blockType === "review") {
		return [12, 10, 8];
	} else {
		if (n <= 9) {
			return [n, n - 1, n - 2];
		} else if (n === 10) {
			return [n, n - 1, n - 3];
		} else {
			return [n, n - 2, n - 4];
		}
	}
};

/**
 * Helper for computing if test block is progress unlocked. Student must star
 * all but two lessons, excluding the last core lesson before the test. The last
 * core lesson before the test must have stars or started at least 3 times if no
 * stars.
 *
 * @param   {[object]}  blocks  blocks in the chapter {requirementLevel:
 * core|enrichment, stars: stars on block, blockID, timesStarted}
 *
 * @return  {boolean}            whether or not test block should be unlocked
 */
const isTestUnlockedByProgress = blocks => {
	if (_.isEmpty(blocks)) return false;
	const coreBlocks = _.filter(blocks, b => isCoreBlock(b));
	if (!coreBlocks.length) {
		return false;
	}
	const numBeforeLastCore = coreBlocks.length - 1;
	const blocksBeforeLastCore = coreBlocks.slice(0, numBeforeLastCore);
	const numStarredBeforeLastCore = _.filter(
		blocksBeforeLastCore,
		c => c.stars > 0
	).length;
	const lastCore = coreBlocks[coreBlocks.length - 1];
	const passedLastCore = lastCore.stars > 0 || lastCore.timesStarted >= 3;
	return numStarredBeforeLastCore >= numBeforeLastCore - 2 && passedLastCore;
};

/**
 * Helper for computing which core blocks to unlock in a chapter. Returns
 * the blockIDs of core blocks to unlock in an unlocked chapter based on
 * core blocks with stars. A core block is progress unlocked if all the core
 * blocks before it have one or more stars.
 * NOTE: Blocks should be in the order they are in the chapter.
 *
 * @param   {[object]}  blocks  {requirementLevel: core|enrichment, stars: stars on block, blockID}
 *
 * @return  {array}            blockIDs of unlocked core blocks
 */
const getUnlockedCoreBlockIDs = (blocks, isChapterUnlocked) => {
	// Core blocks include test blocks, which have no requirementLevel set
	const coreBlocks = _.filter(
		blocks,
		b => !b.requirementLevel || isCoreBlock(b)
	);
	return _.map(
		_.filter(coreBlocks, (b, i) => {
			if (i < INITIAL_UNLOCKED_BLOCK_COUNT && isChapterUnlocked) return true;
			if (b.blockType === "test") {
				// This is a test block. Only unlocked if all core blocks before it are
				// unlocked.
				return isTestUnlockedByProgress(_.slice(coreBlocks, 0, i));
			}
			return (
				i > 0 &&
				(coreBlocks[i - 1].stars > 0 || coreBlocks[i - 1].timesStarted >= 3)
			);
		}),
		cb => cb.blockID
	);
};

/**
 * Helper for computing which enrichment blocks to unlock in a chapter. Returns
 * the block IDs of enrichment blocks to unlock in an unlocked chapter based on
 * stars with blocks and block types. Note that even though test blocks are "Bonus
 * Blocks", we will ignore them in this method since their unlock behavior is
 * handled already in getUnlockedCoreBlockIDs.
 *   A "chunk" of enrichment blocks unlock all at once if immediately preceding
 *   core block has at least MIN_STARS_PER_BLOCK_FOR_ENRICHMENT stars. Note that
 *   this allows earlier core blocks in a group to have no stars and still unlock
 *   the enrichment lessons, as long as the last core block in the group is unlocked.
 * NOTE: Blocks should be in the order they are in the chapter.
 *
 * @param {[object]} blocks {requirementLevel: core|enrichment, stars: stars on block, blockID}
 *
 * @return {array} blockIDs of unlocked enrichment blocks
 */
const getUnlockedEnrichmentBlockIDs = blocks => {
	const blocksByChunk = sortBlocksByChunk(blocks);
	let unlockedBlockIDs = [];
	for (let i = 0; i < blocksByChunk.length; ++i) {
		const group = blocksByChunk[i];
		if (!isBonusBlock(_.first(group).requirementLevel)) continue;
		if (isTestBlock(_.first(group))) continue;
		const prevCoreGroup = _.findLast(_.slice(blocksByChunk, 0, i), chunk =>
			isCoreBlock(_.first(chunk))
		);
		if (prevCoreGroup.length === 0) continue;
		const passedLastBlockInCore =
			_.last(prevCoreGroup).stars >= MIN_STARS_PER_BLOCK_FOR_ENRICHMENT;

		const enrichmentBlockIDs = group.map(block => block.blockID);
		const hasStarredEnrichment = _.some(group, eb => eb.stars);
		if (passedLastBlockInCore || hasStarredEnrichment) {
			unlockedBlockIDs = unlockedBlockIDs.concat(enrichmentBlockIDs);
		}
	}
	return unlockedBlockIDs;
};

/**
 * Helper for grouping an array of block objects by their block type, preserving their order.
 * For example, blocks CCC EE CC EEE groups into:
 * [
 *   0: [CCC],
 *   1: [EE],
 *   2: [CC],
 *   3: [EEE],
 * ]
 *
 * @param   {[object]}  blocks  {requirementLevel: core|enrichment|bonus|mastery, stars: stars on block}
 *
 * @return	{[[object]]}
 */
const sortBlocksByChunk = blocks => {
	const blocksByChunk = [];
	blocks.forEach((block, i) => {
		if (i === 0) {
			blocksByChunk[0] = [block];
		} else if (
			isCoreBlock(block) === isCoreBlock(blocks[i - 1]) &&
			// Separate test blocks from enrichment lessons (aka beast lessons).
			isTestBlock(block) === isTestBlock(blocks[i - 1])
		) {
			_.last(blocksByChunk).push(block);
		} else {
			blocksByChunk.push([block]);
		}
	});
	return blocksByChunk;
};

/**
 * Helper to determine if a block or requirementLevel is a core type.
 *
 * @param {string|object} type Block or requirementLevel
 *
 * @return {boolean}
 */
const isCoreBlock = type => {
	if (_.isObject(type)) {
		type =
			_.get(type, "model.requirementLevel") || _.get(type, "requirementLevel");
	}
	return type === "core" || type === "mastery";
};

// Checks whether a block is an "optional" bonus block
// based on its requirement level.
const isBonusBlock = type => {
	if (_.isObject(type)) {
		type =
			_.get(type, "model.requirementLevel") || _.get(type, "requirementLevel");
	}
	return type === "enrichment" || type === "bonus";
};

/**
 * Helper to determine if a block or blockType is a test type.
 *
 * @param {string|object} type Block or blockType
 *
 * @return {boolean}
 */
const isTestBlock = type => {
	if (_.isObject(type)) {
		type = _.get(type, "model.blockType") || _.get(type, "blockType");
	}
	return type === "test";
};

/**
 * **USED FOR VC ONLY**
 * Helper function for computing the number of blocks
 * @param {[object]} blocks Array with block data
 * @returns {number} Number of downstairs blocks that need to be starred for the test to unlock
 */
const getBlocksUntilTestUnlock = blocks => {
	// Get number of lessons student needs to complete to unlock the test (VC only)
	const numCoreBlocks = blocks.filter(
		block => block.starsAvailable > 0 && isCoreBlock(block)
	).length;
	const numStarredBlocks = blocks.filter(
		block => block.starsObtained > 0 && isCoreBlock(block)
	).length;

	return numCoreBlocks - numStarredBlocks;
};

/**
 * Helper for computing which blocks to unlock in a chapter.
 *
 * Note: blocksUnlockedData should be in the order they are in the chapter
 *
 * @param   {[object]}  blocksUnlockedData  Array with block data. Objects have {blockID, requirementLevel, blockType, stars}
 *
 * @return  {[number]}                      Array of unlocked block IDs
 */
const getUnlockedBlockIDs = function(blocksUnlockedData, isChapterUnlocked) {
	const coreUnlockedBlockIDs = getUnlockedCoreBlockIDs(
		blocksUnlockedData,
		isChapterUnlocked
	);
	const enrichmentUnlockedBlockIDs = getUnlockedEnrichmentBlockIDs(
		blocksUnlockedData
	);
	return _.concat(coreUnlockedBlockIDs, enrichmentUnlockedBlockIDs);
};

// Helper for computing which blocks to unlock in a lab type. Pass in an
// array of the length of the blocks with true for unlocked blocks and false for
// locked blocks, an array of the length of the blocks with the celestial ID of each block,
// and the celestial ID of the block that was just finished.
// Note: blocksUnlockedData should only include progressLock status booleans (not lockOverride unlocked)
const getUnlockedLabBlocks = function(
	blocksUnlockedData,
	blocksCelestialData,
	celestialID
) {
	// The ID of the next celestial after this block
	const nextCelestialID =
		_.min(_.filter(blocksCelestialData, cID => cID > celestialID)) || 0;

	return blocksUnlockedData.map(
		(isAlreadyUnlocked, i) =>
			isAlreadyUnlocked || blocksCelestialData[i] <= nextCelestialID
	);
};

// Helper for detecting if a chapter/grade should be shown as unlocked,
// taking into account lockOverride status.
// Layers are as follows: student overrides > class overrides > student progress
const isUnlocked = function(obj) {
	// studentData is an object
	// classesData is an array of objects
	const {studentData, classesData} = obj;

	if (!studentData && !classesData) {
		return false;
	}

	const classOverrides = _.filter(classesData, cd => !!cd.lockOverride);

	let lockInfo;

	if (studentData && studentData.lockOverride) {
		lockInfo = {
			unlocked: studentData.lockOverride === "unlocked",
			type: "override",
		};
	} else if (classesData && classOverrides.length) {
		// for multiple classes, the order of precendence is unlocked > locked
		lockInfo = {
			unlocked: classOverrides.some(co => co.lockOverride === "unlocked"),
			type: "override",
		};
	} else if (studentData && studentData.progressLock) {
		lockInfo = {
			unlocked: studentData.progressLock === "unlocked",
			type: "progress",
		};
	} else {
		lockInfo = {unlocked: false, type: "progress"};
	}

	return lockInfo;
};

/**
 * @param {Object} blockModel Object with at least {lockOverride, progressLock, and unlocked}.
 * @returns {Boolean}
 */
const blockIsUnlocked = blockModel => {
	if (!_.isObject(blockModel)) return false;

	const {lockOverride} = blockModel;
	if (lockOverride) {
		return lockOverride === "unlocked";
	} else {
		const {unlocked, progressLock} = blockModel;
		return unlocked || progressLock === "unlocked";
	}
};

const shouldCountStars = function(blockData) {
	return blockData.stars > 0 || blockData.timesCompleted > 0;
};

const getNumBlocks = function(grade, chapterIndex) {
	return GradeChapter.getNumBlocksInChapter(grade, chapterIndex);
};

const getNumCoreBlocks = function(blocksData) {
	return _.filter(blocksData, b => isCoreBlock(b)).length;
};

// Helper for computing which special blocks to unlock in a chapter. Pass in:
// grade: the grade the blocks & this chapter are in
// chapterIndex: the index of the chapter within the grade
// blocksWithStars: number of normal blocks that have stars
// testStars: number of stars on test
// blocksData: array of blocks with {blockID, blockType, requirementLevel, stars}
// specialBlockData: an object keyed by review/test/challenge with booleans on whether or not they're progressLock unlocked
// Returns an object with review, test, and challenge booleans representing whether each block should be progressLock unlocked.
const getSpecialUnlockedBlocks = function(props = {}) {
	const {
		grade,
		chapterIndex,
		blocksWithStars,
		testStars,
		specialBlockData = {},
		blocksData,
	} = props;
	const numBlocks = getNumBlocks(grade, chapterIndex);
	//IMPORTANT: If this calculation changes in any way, update
	//src/trainer/util/CopyTable.js:forBlockLocked to reflect the unlocking system.
	return {
		review: specialBlockData.review || blocksWithStars >= numBlocks - 4,
		test:
			specialBlockData.test ||
			(!specialBlockData.suppressUnlockFromProgress &&
				(isTestUnlockedByProgress(blocksData) || testStars > 0)),
		// For reference when later switching review back on: [T1723] When
		// doing that, also update the tests for this in test/shared/trainerCore.js
		// If we start using testStars again, go fix finalTestStars value in
		// UpdateTrainer doUnlockingWithinChapter (we default it to 0 if
		// useCurrentValues is set in inputs to that function)
		challenge:
			specialBlockData.challenge ||
			testStars >= 1 ||
			blocksWithStars >= numBlocks - 4,
	};
};

// Below are helpers for computing whether the next chapter or grade should be unlocked. Pass in
// grade: the grade this chapter is in
// chapterNumber: the number of this chapter
// blocksWithStars: number of normal blocks that have stars in this chapter
// testStars: number of stars on test in this chapter
// timesTestCompleted: number of times test was completed (does not count times //    quit) on test in this chapter
// Returns a boolean indicating whether the next of those things should be progress unlocked.

const unlockNextChapter = function({
	grade,
	chapterNumber,
	testStars,
	testTimesCompleted,
	passedChapter,
} = {}) {
	return (
		(grade < 5 || chapterNumber < 12) &&
		(testStars >= 1 || testTimesCompleted >= 2 || !!passedChapter)
	);
};

const unlockNextGrade = function({
	grade,
	chapterNumber,
	testStars,
	timesTestCompleted,
	passedChapter,
} = {}) {
	return (
		grade < 5 &&
		chapterNumber === 12 &&
		(testStars >= 1 || timesTestCompleted >= 2 || !!passedChapter)
	);
};

// Helper for calculating score in test blocks, given an array of trues and
// falses denoted correct and incorrect answers, respectively.
const getTestScore = function(outcomes) {
	let score = outcomes.slice(0, -5).reduce(function(sum, v) {
		if (v) sum++;
		return sum;
	}, 0);
	// the last 5 count for 2 points
	score += outcomes.slice(-5).reduce(function(sum, v) {
		if (v) sum += 2;
		return sum;
	}, 0);
	return score;
};

// Helper for generating bot outcomes, requires the block and its setList as one of its properties
const getBotOutcomes = function(block) {
	const numProblems = block.setList.problemsPerSet;
	// vary the bots' actual scores by a certain value
	const variance = 2;
	const starCutoffs =
		block.setList.starCutoffs ||
		getDefaultStarCutoffs(block.blockType, block.setList.problemsPerSet);
	const cutoffs = starCutoffs
		.map(v => v + variance - Math.floor(Math.random() * 2 * variance))
		.sort();
	// two bots' cutoffs shouldn't equal each other
	if (cutoffs[1] === cutoffs[2]) cutoffs[1]--;
	if (cutoffs[0] === cutoffs[1]) cutoffs[0]--;
	// make sure bots aren't 2 apart
	if (cutoffs[2] === cutoffs[1] + 2) cutoffs[1]--;
	if (cutoffs[1] === cutoffs[0] + 2) cutoffs[0]--;

	// start with all correct answers and adjust downward
	let botOutcomes = _.range(3).map(() => _.fill(Array(numProblems), true));
	// assigning probabilities per problem
	const botWeights = [
		// Bot 1: worse at beginning, gets 1 star
		_.range(numProblems).map(v =>
			Math.ceil((100 * (numProblems - v)) / numProblems)
		),
		// Bot 2: worse at end, gets 2 stars
		_.range(numProblems).map(v =>
			Math.ceil(100 * (1 - (numProblems - v) / numProblems))
		),
		// Bot 3: randomly throughout, gets 3 stars
		_.fill(Array(numProblems), Math.ceil(100 * (1 / numProblems))),
	];

	// choose outcomes for each bot
	botOutcomes = botOutcomes.map(function(outcome, idx) {
		let i;
		const weights = botWeights[idx];
		let pointsWrong = 0;
		const maxPointsWrong = getTestScore(outcome) - cutoffs[idx];

		while (pointsWrong < maxPointsWrong) {
			// if we already have four wrong in the first ten, weight the first ten 0
			if (outcome.slice(0, 10).reduce((sum, v) => (sum += v ? 0 : 1), 0) > 4) {
				for (i = 0; i < 10; i++) weights[i] = 0;
			}

			// if we only need one point, exclude the last five
			if (maxPointsWrong - pointsWrong === 1) {
				for (i = 0; i < 5; i++) weights[weights.length - i - 1] = 0;
			}

			const weightTotal = weights.reduce((sum, v) => (sum += v), 0);
			const randomWeight = Math.floor(Math.random() * weightTotal);
			let weightSum = weights[0];
			let problemIndex = 0;
			while (weightSum < randomWeight) {
				weightSum += weights[problemIndex];
				problemIndex++;
			}

			outcome[Math.min(numProblems - 1, problemIndex)] = false;
			pointsWrong += numProblems - problemIndex - 1 < 5 ? 2 : 1;
		}

		return _.shuffle(outcome);
	});

	const possibleBots = _.sampleSize(
		["winniebot", "alexbot", "lizziebot", "groggbot"],
		3
	);

	return {bots: possibleBots, outcomes: botOutcomes};
};

//TODO: need to take into account ties on the last problem.
const getBotTrials = function(problem, botOutcomes, problemProcessor) {
	const problemModel = problem.model;
	let botWrongAnswers = problemModel.botWrongAnswers;
	botWrongAnswers = _.pull(botWrongAnswers, "");
	//If student gets a bot wrong answer, make that answer more likely answered by the bot.
	const botTrials = _.map(botOutcomes, (isCorrect, i) => {
		// TODO: Malformed problems with no wrong answers should just make the bots always right.
		if (isCorrect || !botWrongAnswers.length) {
			if (problemProcessor.getBotRightAnswer) {
				return problemProcessor.getBotRightAnswer(problemModel);
			} else {
				throw new Error(
					"Test problem #" +
						problem.problemID +
						" with renderer " +
						problem.model.rendererID +
						" does not have a getBotRightAnswer function."
				);
			}
		} else {
			if (botWrongAnswers.indexOf(problem.trials[0]) > -1) {
				//TODO: should use _.isEqual
				return _.sample([
					problem.trials[0],
					problem.trials[0],
					_.sample(botWrongAnswers),
				]);
			} else {
				return _.sample(botWrongAnswers);
			}
		}
	});
	return botTrials;
};

const getStarBucks = function(numStars) {
	let bucks = 0;
	let currStar = 1;
	while (currStar <= numStars) {
		bucks += BUCKS_FOR_STARS[currStar - 1];
		currStar++;
	}
	return bucks;
};

// Get bucks earned for nth star earned (n = 1, 2, 3)
const getBucksForNthStar = function(n) {
	return BUCKS_FOR_STARS[n - 1];
};

// 50 XP per star
const getFinishBlockXP = function(oldStars, newStars) {
	return Math.max(newStars - oldStars, 0) * STAR_XP;
};

const getFinishMiscBlockXP = function(block, isTutorial = false) {
	const miscBlockName = _.get(block, ["model", "miscBlockName"], null);
	if (miscBlockName === "tutorial" || isTutorial) {
		return TUTORIAL_BLOCK_XP;
	}

	return 0;
};

const getFinishLabBlockXP = function(celestialID) {
	return XP_FOR_CELESTIALS[celestialID];
};

const getFinishLabBlockBucks = function(celestialID) {
	return BUCKS_FOR_CELESTIALS[celestialID];
};

const getFinishBlockBucks = function(oldStars, newStars) {
	return Math.max(0, getStarBucks(newStars) - getStarBucks(oldStars));
};

const getFinishMiscBlockBucks = function(block, isTutorial = false) {
	const miscBlockName = _.get(block, ["model", "miscBlockName"], null);
	if (miscBlockName === "tutorial" || isTutorial) {
		return TUTORIAL_BLOCK_BUCKS;
	}

	return 0;
};

const getFinishTrophyBucks = function() {
	return 5;
};

// XP for finish reading a chapter in book
const getReadChapterXP = function() {
	return 100;
};

// XP for finish watching a video in theater
const getWatchedVideoXP = function() {
	return 10;
};

// A student can earn bucks when reaching a new level.
// Currently, getting to level n will earn them n more bucks.
// Going from level 3 to level 4 will earn you 4 bucks.
// Going from level 3 to level 5 will earn you 4 + 5 = 9 bucks. (Though there might not be a case where you can earn enough XP to jump multiple levels at once.)
const getBucksFromXP = function(oldXP, newXP) {
	const oldLevel = getLevel(oldXP);
	const newLevel = getLevel(newXP);
	if (newLevel > oldLevel) {
		return _.sum(_.range(oldLevel + 1, newLevel + 1));
	} else {
		return 0;
	}
};

//Level formula is level n takes 25 * n^2 XP with the exception of 1 taking 0 xp
const getLevel = function(xp) {
	return Math.max(1, Math.floor(Math.sqrt(xp / 25)));
};

const getLevelCutoff = function(level) {
	return level === 1 ? 0 : 25 * level * level;
};

const getLevelXP = function(xp) {
	const currLevel = getLevel(xp);
	const nextLevel = currLevel + 1;
	const currCutoffXP = getLevelCutoff(currLevel);
	const nextCutoffXP = getLevelCutoff(nextLevel);
	return {
		currLevel,
		nextLevel,
		currLevelXP: xp - currCutoffXP,
		totalLevelXP: nextCutoffXP - currCutoffXP,
	};
};

const getReviewProblemsBeforeQuitOption = function() {
	return 20;
};

const getTheaterSetPercentComplete = function(videoModels) {
	const totalVideos = _.size(videoModels);
	let numerator = 0;
	_.each(videoModels, v => {
		numerator += v.isWatched ? 1 : v.position;
	});
	return numerator / totalVideos;
};

const updateLastPlayedData = function(oldLastPlayedData, playedData) {
	let chapters = (oldLastPlayedData && oldLastPlayedData.chapters) || [];
	if (playedData.chapterID) {
		const nextChapterID = playedData.chapterID;
		chapters = _.uniq([nextChapterID].concat(chapters));
		if (chapters.length > 6) chapters.pop();
	}

	return _.extend(
		oldLastPlayedData,
		{..._.omit(playedData, ["chapterID"])},
		{chapters}
	);
};

// TESTS
const getTestProblemRange = function({problemCount, forBookTest = false}) {
	const ranges = forBookTest ? BOOK_TEST_RANGES : TEST_RANGES;
	return ranges[problemCount];
};

const getTestProblemRangeFromResult = function(result) {
	// Back end raw db result vs front end result structure
	const problems = result.problems || _.get(result, "progress.problems");
	const forBookTest = result.resultType === "bookTest";
	return getTestProblemRange({problemCount: problems.length, forBookTest});
};

const getTestPart2StartIndex = function(result) {
	const range = getTestProblemRangeFromResult(result);
	return range && range[0];
};

const getCurrentTestPart = function(result) {
	// If any have been submitted, you must be on part 2
	const anySubmitted = _.some(result.problems, p =>
		_.includes(["correct", "incorrect", "skipped"], p.outcome)
	);
	return anySubmitted ? 2 : 1;
};

const getCurrentProblemRangeForTest = function(result) {
	const part = getCurrentTestPart(result);
	return getProblemRangeForTestPart(result, part);
};

const getProblemRangeForTestPart = function(result, part) {
	const testRange = getTestProblemRangeFromResult(result);
	if (testRange) {
		return part === 1
			? _.range(testRange[0])
			: _.range(testRange[0], testRange[1]);
	}
	return [];
};

const hasSeenTestPart2 = function(result) {
	const firstIndex = _.first(getProblemRangeForTestPart(result, 2));
	if (!firstIndex) return;
	// If any problem in second part of test has been started/seen
	return _.some(result.problems, (p, i) => i >= firstIndex && p.started);
};

const showTestPart1Decision = function(result) {
	// If they've submitted part 1 (and haven't been shunted to complete & 0 stars by failing)
	// haven't taken 1 star (and marked result complete) and haven't seen part 2
	return (
		// TODO: find a way to avoid hard coding these. We can't require the results manager
		// here because of a cyclical dependency. Currently, we're calling this function in
		// TrainerPage.goToCurrentProblem to check if we should show the part 1 decision
		// given the current state of a result, but we don't know if the thing being tested
		// is a test or bookTest or not. Potentially - figure that out first there and then
		// remove this condition?
		(result.resultType === "test" || result.resultType === "bookTest") &&
		!result.isComplete &&
		_.get(result, "problems.0.outcome") &&
		!hasSeenTestPart2(result)
	);
};

// timeSinceStart is the return of OpsResult.getTimeSinceStart. We can't
// require OpsResult from here though, so it should be passed in.
const getTestInfo = function(result, timeSinceStart, forcePart) {
	const forBookTest = result.resultType === "bookTest";
	const part = forcePart || getCurrentTestPart(result);
	const currentProblemRange = getProblemRangeForTestPart(result, part);
	const currProblems = _.filter(result.problems, (p, i) =>
		_.includes(currentProblemRange, i)
	);
	const numSkipped = currProblems.filter(p => !p.saved).length;
	const totalCurrProblems = currProblems.length;
	const part1Problems = getProblemRangeForTestPart(result, 1);
	const part2Problems = getProblemRangeForTestPart(result, 2);
	const numAnswered = currProblems.length - numSkipped;
	return {
		forBookTest,
		timeSinceStart,
		part,
		currentProblemRange,
		currProblems,
		numSkipped,
		totalCurrProblems,
		part1Problems,
		part2Problems,
		numAnswered,
	};
};

// Takes the getTestInfo return. Returns data indicating what restrictions are
// in place for submitting. Note that we'll need copy updates, e.g.
// TutorialCopy, if we ever have both restrictions at once.
const getTestSubmitRestrictions = function(testInfo) {
	let part1MinAnswered = null;
	let part1MinMinutes = null;
	let part2MinMinutes = null;
	if (testInfo.forBookTest) {
		part1MinMinutes = BOOK_TEST_PART_1_MIN_MINUTES;
		part2MinMinutes = BOOK_TEST_PART_2_MIN_MINUTES;
	} else {
		part1MinAnswered = _.round(0.75 * testInfo.part1Problems.length);
	}
	return {
		part1MinAnswered,
		part1MinMinutes,
		part2MinAnswered: null,
		part2MinMinutes,
	};
};

// Helper for computing which blocks to unlock in a chapter. Pass in the total
// number of stars in the chapter, the total number of blocks with stars, and an
// array of the length of the blocks with true for unlocked blocks and false for
// locked blocks.
// Note: blocksUnlockedData should only include progressLock status booleans (not lockOverride unlocked)
const getUnlockedBlocks = function(
	totalStars,
	blocksWithStars,
	blocksUnlockedData
) {
	const totalToUnlock = getTotalUnlockedBlocks(totalStars, blocksWithStars);
	return blocksUnlockedData.map(
		(isAlreadyUnlocked, i) => isAlreadyUnlocked || i < totalToUnlock
	);
};

// Helper for computing which blocks to unlock in a chapter. Returns the number
// of blocks to unlock in an unlocked chapter based on stars alone. Pass the
// chapter's total stars and number of blocks with stars.
const getTotalUnlockedBlocks = function(totalStars, blocksWithStars) {
	return Math.floor((totalStars + blocksWithStars) / 2) + 2;
};

// Returns one of the following:
// - {canSubmit: true} to indicate the submission can go through.
// - {canSubmit: false, error: "notEnoughSaved", answered, minimum} to
//   indicate more problems need to be saved.
// - {canSubmit: false, error: "notEnoughTime", spent, minimum} to indicate
//   more time needs to be spent. spent and minimum are in minutes.
const checkTestSubmitRestrictions = function(
	testInfo,
	{forcePart = null, ignoreTime = false}
) {
	const part = forcePart || testInfo.part;
	const submitRestrictions = getTestSubmitRestrictions(testInfo);
	const minAnswered = submitRestrictions[`part${part}MinAnswered`];
	if (minAnswered && minAnswered > testInfo.numAnswered) {
		return {
			canSubmit: false,
			error: "notEnoughSaved",
			answered: testInfo.numAnswered,
			minimum: minAnswered,
		};
	}
	const minMinutes = submitRestrictions[`part${part}MinMinutes`];
	if (
		!ignoreTime &&
		minMinutes &&
		testInfo.timeSinceStart &&
		minMinutes > testInfo.timeSinceStart / 60000
	) {
		const currMinutes = Math.min(testInfo.timeSinceStart / 60000);
		return {
			canSubmit: false,
			error: "notEnoughTime",
			spent: currMinutes,
			minimum: minMinutes,
		};
	}
	return {canSubmit: true};
};

const isTestInCooldown = function(results) {
	if (_.isEmpty(results) || results.resultType === "bookTest") {
		return false;
	}
	const lastRes = results[0];
	if (lastRes.finishStatus === "quit") {
		return moment
			.utc()
			.isBefore(
				moment.utc(lastRes.finishedAt).add(TEST_QUIT_COOLDOWN_DAYS, "days")
			);
	} else if (lastRes.finishStatus === "completed" && lastRes.stars === 0) {
		return moment
			.utc()
			.isBefore(
				moment.utc(lastRes.finishedAt).add(TEST_FAIL_COOLDOWN_DAYS, "days")
			);
	}
};

// Computes the test cooldown from a list of results regardless of how the
// list is ordered, mirroring the trainer plugin's getTestCooldownInfo: the
// cooldown comes from the most recently finished result, ordered by
// finishedAt shifted by the result's timezone offset in hours. Returns
// {reason, expiresAt, finishedAtTZ} where reason is "quit" or "fail" and
// expiresAt is a UTC moment, or {} when no cooldown applies. Callers decide
// whether the cooldown is still active (and whether overrides bypass it).
const getTestCooldownFromResults = function(results) {
	const lastFinished = _.maxBy(
		_.filter(results, r => r.finishedAt),
		r =>
			moment
				.utc(r.finishedAt)
				.add(r.finishedAtTZ, "hours")
				.valueOf()
	);
	if (!lastFinished) {
		return {};
	}
	if (lastFinished.finishStatus === "quit") {
		return {
			reason: "quit",
			expiresAt: moment
				.utc(lastFinished.finishedAt)
				.add(TEST_QUIT_COOLDOWN_DAYS, "days"),
			finishedAtTZ: lastFinished.finishedAtTZ,
		};
	} else if (
		lastFinished.finishStatus === "completed" &&
		lastFinished.stars === 0
	) {
		return {
			reason: "fail",
			expiresAt: moment
				.utc(lastFinished.finishedAt)
				.add(TEST_FAIL_COOLDOWN_DAYS, "days"),
			finishedAtTZ: lastFinished.finishedAtTZ,
		};
	}
	return {};
};

// Take daily read activity and sort into chapter
// This is also used by subscriptionWeeklyUpdate cron, so it's here instead of ProfileUtil.
const sortSectionsIntoChapters = function(sectionData) {
	const withGradeChapter = _.map(sectionData, (time, sectionID) => {
		const gradeChapter = ChapterSection.getContextFromSectionID(
			parseInt(sectionID)
		);
		return gradeChapter ? _.extend(gradeChapter, {time}) : null;
	});
	// Filter out sectionIDs that can't be found.
	const groupWith = withGradeChapter.filter(d => d);
	const groupByChapter = _.groupBy(
		groupWith,
		g => g.gradeNumber + "." + g.chapterIndex
	);

	const byChapter = _.map(groupByChapter, sections => {
		const section = sections[0];
		const {gradeNumber, chapterIndex} = section;
		const chapterName = GradeChapter.getChapterName(
			gradeNumber,
			chapterIndex + 1
		);
		return {
			chapterName,
			gradeNumber,
			chapterIndex,
			time: _.sumBy(sections, s => s.time),
		};
	});

	return _.orderBy(byChapter, ["gradeNumber", "chapterIndex"], ["asc", "asc"]);
};

module.exports = {
	MULTISET_RESULT_TYPES,
	TEST_RANGES,
	BOOK_TEST_RANGES,
	DEFAULT_TRIAL_XP,
	INITIAL_UNLOCKED_BLOCK_COUNT,
	STAR_XP,
	TRIAL_XP,
	THROTTLED_TRIAL_XP,
	MAX_TRIALS_XP,
	MAX_CELESTIAL_XP,
	MAX_TRIAL_XP_AFTER_ALL_STARS,
	MAX_COMPLETIONS_BEFORE_XP_LIMITED,
	MAX_COMPLETIONS_ON_3_STAR_TEST_BEFORE_XP_LIMITED,
	getProgressProps,
	genericShouldIntervene,
	getStars,
	getDefaultStarCutoffs,
	getUnlockedCoreBlockIDs,
	getUnlockedEnrichmentBlockIDs,
	sortBlocksByChunk,
	isCoreBlock,
	isBonusBlock,
	isTestBlock,
	getBlocksUntilTestUnlock,
	getUnlockedBlockIDs,
	getUnlockedLabBlocks,
	getUnlockedBlocks,
	isUnlocked,
	blockIsUnlocked,
	shouldCountStars,
	getNumBlocks,
	getNumCoreBlocks,
	getSpecialUnlockedBlocks,
	unlockNextChapter,
	unlockNextGrade,
	getTestScore,
	getBotOutcomes,
	getBotTrials,
	getFinishBlockXP,
	getFinishMiscBlockXP,
	getFinishBlockBucks,
	getFinishMiscBlockBucks,
	getFinishTrophyBucks,
	getBucksForNthStar,
	getBucksFromXP,
	getFinishLabBlockBucks,
	getFinishLabBlockXP,
	getReadChapterXP,
	getWatchedVideoXP,
	getLevel,
	getLevelXP,
	getReviewProblemsBeforeQuitOption,
	getTheaterSetPercentComplete,
	updateLastPlayedData,
	getTestPart2StartIndex,
	getCurrentTestPart,
	getCurrentProblemRangeForTest,
	getProblemRangeForTestPart,
	hasSeenTestPart2,
	showTestPart1Decision,
	getTestInfo,
	getTestSubmitRestrictions,
	checkTestSubmitRestrictions,
	isTestInCooldown,
	getTestCooldownFromResults,
	sortSectionsIntoChapters,
};
